/**
 * Super Admin Dashboard - Platform Management Overview
 *
 * Features:
 * - Platform statistics (clients, users, MRR)
 * - Quick access to client/organization management
 * - Super admin verification
 * - Platform health indicators
 *
 * Access: Super admin users only (user.super_admin === true)
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner, Alert } from 'flowbite-react';
import {
  HiUsers,
  HiOfficeBuilding,
  HiCurrencyDollar,
  HiTrendingUp,
  HiUserGroup,
  HiChartBar
} from 'react-icons/hi';
import { useStatus } from '../contexts/StatusProvider';
import { ClientCompanySelector } from '../components/ClientCompanySelector';
import SystemHealthMonitor from '../components/SystemHealthMonitor';
import AdminActivityLog from '../components/AdminActivityLog';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

interface PlatformStats {
  platform_summary: {
    total_clients: number;
    active_clients: number;
    total_users: number;
    total_mrr: number;
  };
  tier_distribution: {
    OSS: { count: number; percentage: number };
    PRO: { count: number; percentage: number };
    ENTERPRISE: { count: number; percentage: number };
  };
  status_distribution: {
    active: { count: number; percentage: number };
    suspended: { count: number; percentage: number };
    cancelled: { count: number; percentage: number };
  };
  recent_clients: Array<{
    Client_ID: string;
    Client_Name: string;
    Client_Type: string;
    Create_Date: string;
  }>;
}

const SuperAdminDashboard: React.FC = () => {
  const { Is_Super_Admin, Is_Loading: authLoading, Email_Address } = useStatus();
  const navigate = useNavigate();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Redirect if not super admin
  useEffect(() => {
    if (!authLoading && !Is_Super_Admin) {
      console.log('[SuperAdminDashboard] User is not super admin, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [Is_Super_Admin, authLoading, navigate]);

  // Fetch platform statistics
  useEffect(() => {
    if (Is_Super_Admin) {
      fetchPlatformStats();
    }
  }, [Is_Super_Admin]);

  const fetchPlatformStats = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[SuperAdminDashboard] Fetching platform stats');

      const response = await Make_Authorized_API_Call<PlatformStats>(
        API_ENDPOINTS.SUPERADMIN.PLATFORM_STATS,
        'GET'
      );

      console.log('[SuperAdminDashboard] Platform stats response:', {
        success: response.success,
        recordCount: isSuccessResponse(response) ? response.data?.Records?.length : 0
      });

      if (!isSuccessResponse(response)) {
        throw new Error(response.message || 'API returned unsuccessful response');
      }

      if (!response.data?.Records || response.data.Records.length === 0) {
        throw new Error('No platform statistics available');
      }

      setStats(response.data.Records[0]);
      console.log('[SuperAdminDashboard] Stats loaded successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load platform statistics';
      console.error('[SuperAdminDashboard] Error fetching platform stats:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state during auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="xl" />
      </div>
    );
  }

  // Don't render if not super admin (redirect will happen)
  if (!Is_Super_Admin) {
    return null;
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Platform Overview</h2>
        <p className="text-gray-600">Monitor platform health and access administrative functions</p>
      </div>

      {/* Quick Actions */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/super/clients')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Manage</p>
              <p className="text-lg font-bold text-gray-900">Clients</p>
            </div>
            <HiOfficeBuilding className="w-10 h-10 text-blue-500" />
          </div>
        </Card>

        <Card className="bg-white hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/super/users')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Manage</p>
              <p className="text-lg font-bold text-gray-900">Users</p>
            </div>
            <HiUsers className="w-10 h-10 text-purple-500" />
          </div>
        </Card>

        <Card className="bg-white hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/super/organizations')}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Manage</p>
              <p className="text-lg font-bold text-gray-900">Organizations</p>
            </div>
            <HiUserGroup className="w-10 h-10 text-green-500" />
          </div>
        </Card>

        <Card className="bg-white hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">View</p>
              <p className="text-lg font-bold text-gray-900">System Health</p>
            </div>
            <div className="w-10 h-10 flex items-center justify-center">
              <div className="w-4 h-4 bg-green-500 rounded-full animate-pulse"></div>
            </div>
          </div>
        </Card>
      </div>

      {/* Client & Company Selector */}
      <div className="mb-6">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Switch to Client View</h3>
            <p className="text-sm text-gray-600">Select a client and company to view their dashboard and data</p>
          </div>
          <ClientCompanySelector />
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert color="failure" className="mb-6">
          <div>
            <p className="font-semibold">Error loading platform statistics</p>
            <p className="text-sm mt-1">{error}</p>
            <Button
              size="xs"
              color="light"
              className="mt-2"
              onClick={fetchPlatformStats}
            >
              Retry
            </Button>
          </div>
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <Spinner size="xl" />
        </div>
      )}

      {/* Platform Statistics */}
      {!loading && stats && (
        <>
          {/* System Health Monitor */}
          <div className="mb-6">
            <SystemHealthMonitor />
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Total Clients */}
            <Card className="bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Clients</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.platform_summary.total_clients}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.platform_summary.active_clients} active
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <HiOfficeBuilding className="w-8 h-8 text-blue-600" />
                </div>
              </div>
            </Card>

            {/* Active Clients */}
            <Card className="bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Active Clients</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">
                    {stats.platform_summary.active_clients}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.platform_summary.total_clients > 0
                      ? Math.round((stats.platform_summary.active_clients / stats.platform_summary.total_clients) * 100)
                      : 0}% of total
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <HiTrendingUp className="w-8 h-8 text-green-600" />
                </div>
              </div>
            </Card>

            {/* Total Users */}
            <Card className="bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Users</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    {stats.platform_summary.total_users}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Across all clients
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <HiUsers className="w-8 h-8 text-purple-600" />
                </div>
              </div>
            </Card>

            {/* Total MRR */}
            <Card className="bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total MRR</p>
                  <p className="text-3xl font-bold text-gray-900 mt-1">
                    ${stats.platform_summary.total_mrr.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Monthly recurring revenue
                  </p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <HiCurrencyDollar className="w-8 h-8 text-yellow-600" />
                </div>
              </div>
            </Card>
          </div>

          {/* System Health & Activity Log */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Recent Activity Log */}
            <AdminActivityLog limit={5} />
          </div>

          {/* Distribution Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Tier Distribution */}
            <Card className="bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Tier Distribution</h3>
                <HiChartBar className="w-6 h-6 text-gray-400" />
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">OSS</span>
                    <span className="text-sm text-gray-600">
                      {stats?.tier_distribution?.OSS?.count || 0} ({Math.round(stats?.tier_distribution?.OSS?.percentage || 0)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-gray-500 h-2 rounded-full"
                      style={{ width: `${stats?.tier_distribution?.OSS?.percentage ?? 0}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">PRO</span>
                    <span className="text-sm text-gray-600">
                      {stats?.tier_distribution?.PRO?.count ?? 0} ({Math.round(stats?.tier_distribution?.PRO?.percentage ?? 0)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${stats?.tier_distribution?.PRO?.percentage ?? 0}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">ENTERPRISE</span>
                    <span className="text-sm text-gray-600">
                      {stats?.tier_distribution?.ENTERPRISE?.count ?? 0} ({Math.round(stats?.tier_distribution?.ENTERPRISE?.percentage ?? 0)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{ width: `${stats?.tier_distribution?.ENTERPRISE?.percentage ?? 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Status Distribution */}
            <Card className="bg-white">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Status Distribution</h3>
                <HiChartBar className="w-6 h-6 text-gray-400" />
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">Active</span>
                    <span className="text-sm text-gray-600">
                      {stats?.status_distribution?.active?.count ?? 0} ({Math.round(stats?.status_distribution?.active?.percentage ?? 0)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${stats?.status_distribution?.active?.percentage ?? 0}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">Suspended</span>
                    <span className="text-sm text-gray-600">
                      {stats?.status_distribution?.suspended?.count ?? 0} ({Math.round(stats?.status_distribution?.suspended?.percentage ?? 0)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-yellow-500 h-2 rounded-full"
                      style={{ width: `${stats?.status_distribution?.suspended?.percentage ?? 0}%` }}
                    />
                  </div>
                </div>
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">Cancelled</span>
                    <span className="text-sm text-gray-600">
                      {stats?.status_distribution?.cancelled?.count ?? 0} ({Math.round(stats?.status_distribution?.cancelled?.percentage ?? 0)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-red-500 h-2 rounded-full"
                      style={{ width: `${stats?.status_distribution?.cancelled?.percentage ?? 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Recent Clients */}
          <Card className="bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Clients</h3>
              <Button
                size="sm"
                color="light"
                onClick={() => navigate('/super/clients')}
              >
                View All Clients
              </Button>
            </div>
            {stats.recent_clients && stats.recent_clients.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_clients.map((client) => (
                  <div
                    key={client.Client_ID}
                    className="flex items-center justify-between p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{client.Client_Name}</p>
                      <p className="text-sm text-gray-500 font-mono">{client.Client_ID}</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Badge
                        color={
                          client.Client_Type === 'OSS'
                            ? 'gray'
                            : client.Client_Type === 'PRO'
                            ? 'info'
                            : 'purple'
                        }
                      >
                        {client.Client_Type}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {new Date(client.Create_Date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent clients</p>
            )}
          </Card>

        </>
      )}
    </main>
  );
};

export default SuperAdminDashboard;
